import { downloadFileWithReportKey } from '@faceup/crypto'
import { UntitledIcon } from '@faceup/icons'
import { ulEyeOff } from '@faceup/icons/ulEyeOff'
import { usAttachment01 } from '@faceup/icons/usAttachment01'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { TextUrlfy, twMerge, UserAvatar } from '@faceup/ui'
import { Space, Tooltip, Typography, useUiBaseConfig } from '@faceup/ui-base'
import { captureException } from '@sentry/react'
import moment from 'moment-timezone'
import { type ComponentProps, memo, useContext } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { useMarkMessagesAsRead } from '../../hooks/useMarkMessagesAsRead'
import { useSuspenseChatMessage } from '../../hooks/useSuspenseChatMessage'
import { ChatAttachmentList } from '../AttachmentList'
import { TextToRedact, UsernameAvatarRedact, UsernameRedact } from '../Redaction'
import { RelativeMessagesContext } from './RelativeMessagesContext'

const fragments = {
  FollowUpChatMessage_followUpActivityNode: graphql(`
    fragment FollowUpChatMessage_followUpActivityNode on CompanyReportFollowUpComment {
      id
      author {
        ... on CompanyVictim {
          id
        }

        ... on Member {
          id
          name
          profileImageUrl
        }
      }
      translation(sourceLanguage: $sourceLanguage, targetLanguage: $targetLanguage) {
        ciphertext
        nonce
        sourceLanguage
        targetLanguage
      }
      isAuthorRedacted
      isNameHidden
      deletedAt
      readByMemberIds
      isMessageReadByVictim
      case {
        id
        updatedAt
        lastChangeDate @include(if: $isVictim)
        ... on CompanyReport {
          translation {
            id
            body
            bodyNonce
          }
        }
        encryptionKey
        organizationalUnit {
          id
          organizationalUnitName
          mother {
            id
          }
        }
      }
      attachments {
        id
        name
        url
        mimetype
      }
      createdAt

      ...UseMarkMessagesAsReadFragments_comment
    }
  `),
}

const messages = defineMessages('report.components.chat.followUpChatMessage', [
  { id: 'anonymous', defaultMessage: 'Anonymous' },
  { id: 'notSeenYet', defaultMessage: 'Received' },
  { id: 'read', defaultMessage: 'Seen' },
  { id: 'anonymizedUser', defaultMessage: 'Member' },
] as const)

type FollowUpChatMessageProps = {
  orientation: 'left' | 'right'
  message: FragmentType<typeof fragments.FollowUpChatMessage_followUpActivityNode>
  fileDownloadHandler?: (blob: Blob, name: string) => Promise<void>
  onMessageSeen?: () => void
}

const FollowUpChatMessageInner = ({
  orientation,
  message: _message,
  fileDownloadHandler,
  onMessageSeen,
}: FollowUpChatMessageProps) => {
  const message = getFragmentData(fragments.FollowUpChatMessage_followUpActivityNode, _message)
  const relativeMessages = useContext(RelativeMessagesContext)
  const { token } = useUiBaseConfig()
  const { formatMessage } = useIntl()
  const isFollowUp = relativeMessages.type === 'Victim'

  const author = message?.author
  const authorType = author?.__typename
  const report = message.case?.__typename === 'CompanyReport' ? message.case : null

  const isViewersMessage =
    (authorType === 'CompanyVictim' && isFollowUp) || (authorType === 'Member' && !isFollowUp)

  const { ref } = useMarkMessagesAsRead({
    message,
    onCompleted: () => onMessageSeen?.(),
  })

  const { text, victimName } = useSuspenseChatMessage({
    ciphertext: message?.translation?.ciphertext ?? '',
    nonce: message?.translation?.nonce ?? '',
    encryptionKey: message?.case?.encryptionKey ?? '',
    // Only reporter-authored messages need the (encrypted) name decrypted;
    // member names come straight from the GraphQL data.
    victimReport:
      authorType === 'CompanyVictim' && report
        ? {
            body: report.translation?.body ?? '',
            bodyNonce: report.translation?.bodyNonce ?? '',
          }
        : null,
  })

  const resolveAuthorName = () => {
    if (author?.__typename === 'Member') {
      if (message?.isAuthorRedacted) {
        return formatMessage(messages.anonymizedUser)
      }
      return author.name ?? message.case?.organizationalUnit?.organizationalUnitName ?? ''
    }
    if (author?.__typename === 'CompanyVictim') {
      return victimName || formatMessage(messages.anonymous)
    }
    return ''
  }
  const authorName = resolveAuthorName()

  return (
    <div
      className={twMerge(
        'flex items-start justify-end gap-2',
        orientation === 'right' ? 'me-3 flex-row' : 'me-0 flex-row-reverse'
      )}
    >
      <div className='inline-flex max-w-[80%] flex-col flex-nowrap pb-5'>
        <div
          style={{
            background:
              message?.author?.__typename === 'CompanyVictim' ? token.primary1 : '#f0f3f4',
            whiteSpace: 'pre-line',
            overflow: 'hidden',
            wordBreak: 'break-word',
            borderRadius: orientation === 'right' ? '12px 12px 2px' : '12px 12px 12px 2px',
          }}
          data-test='message-text'
        >
          <Typography.Text className='block px-4 pt-2 pb-0.5 font-semibold text-xs leading-5'>
            {message?.isNameHidden ? (
              <Tooltip
                title={
                  relativeMessages.type === 'Admin' && (
                    <FormattedMessage message={relativeMessages.nameHidden} />
                  )
                }
                placement='top'
              >
                <Space>
                  <UsernameRedact
                    type={
                      message?.author?.__typename === 'Member'
                        ? {
                            type: 'chatMemberUsername',
                            id: message.author?.id ?? '',
                          }
                        : {
                            type: 'sender',
                          }
                    }
                    text={authorName}
                  >
                    {authorName}
                  </UsernameRedact>
                  <UntitledIcon icon={ulEyeOff} />
                </Space>
              </Tooltip>
            ) : (
              <UsernameRedact
                type={
                  message?.author?.__typename === 'Member'
                    ? {
                        type: 'chatMemberUsername',
                        id: message.author?.id ?? '',
                      }
                    : {
                        type: 'sender',
                      }
                }
                text={authorName}
              >
                <span className='sensitive'>{authorName}</span>
              </UsernameRedact>
            )}
          </Typography.Text>
          {text && (
            <div className='sensitive max-w-[600px] px-4 pt-0.5 pb-2'>
              <TextToRedact
                type={{
                  type: 'chatMessages',
                  id: message?.id ?? '',
                }}
                text={text}
              >
                <TextUrlfy text={text} />
              </TextToRedact>
            </div>
          )}
          <ChatAttachmentList
            attachments={message?.attachments ?? []}
            icons={{
              attachment: usAttachment01,
            }}
            download={async attachment => {
              const result = await downloadFileWithReportKey(
                attachment,
                message?.case?.encryptionKey ?? '',
                fileDownloadHandler
              )
              if (result.isErr()) {
                captureException(result.error.exception, {
                  tags: { type: 'file_decryption' },
                  extra: {
                    flow: 'chat-attachment-download',
                    messageId: message?.id,
                    caseId: message?.case?.id,
                    attachmentId: attachment.id,
                    fileName: attachment.name,
                    mimetype: attachment.mimetype,
                  },
                })
              }
              return result
            }}
          />
        </div>
        <div
          className={twMerge(
            'flex flex-nowrap items-center justify-start gap-1 p-1',
            orientation === 'right' ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          {isViewersMessage && (
            <>
              <MessageFooterContent>
                {(authorType === 'Member' && message?.isMessageReadByVictim) ||
                (authorType === 'CompanyVictim' && message?.readByMemberIds.length) ? (
                  <FormattedMessage message={messages.read} />
                ) : (
                  <FormattedMessage message={messages.notSeenYet} />
                )}
              </MessageFooterContent>
              <div className="px-0.5 text-colorTextSecondary before:content-['•']" />
            </>
          )}
          <MessageFooterContent
            // Should be referenced at bottom of the message.
            // Long messages may not fit entirely within the visible area and might not be marked as read.
            ref={ref}
          >
            {moment(message?.createdAt).format('LLL')}
          </MessageFooterContent>
        </div>
      </div>
      <UsernameAvatarRedact
        type={
          message?.author?.__typename === 'Member'
            ? {
                type: 'chatMemberUsername',
                id: message.author?.id ?? '',
              }
            : {
                type: 'sender',
              }
        }
        text={authorName}
      >
        <UserAvatar
          className='min-w-8 print:hidden'
          user={{ ...message?.author, name: authorName }}
        />
      </UsernameAvatarRedact>
    </div>
  )
}

// TODO: styled leftover. Reused element extracted so its shared classes live in one
// place. Ideally done differently, kept like this to minimize breaking changes.
const MessageFooterContent = ({ className, ...props }: ComponentProps<'span'>) => (
  <span className={twMerge('text-colorTextSecondary text-xs leading-4', className)} {...props} />
)

export const FollowUpChatMessage = memo(FollowUpChatMessageInner)
